import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { docToRental, RENTAL_DETAIL_COLUMNS, type Rental } from '../lib/rental';

export function useRental(id: string | undefined): { rental: Rental | null; loading: boolean } {
    const [rental, setRental] = useState<Rental | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetch = async () => {
            setLoading(true);
            setRental(null);
            if (!id) {
                if (!cancelled) {
                    setRental(null);
                    setLoading(false);
                }
                return;
            }

            const { data, error: slugError } = await supabase
                .from('rental_aliases')
                .select(RENTAL_DETAIL_COLUMNS)
                .eq('requested_slug', id)
                .maybeSingle();
            if (cancelled) return;
            if (data) {
                setRental(docToRental(data));
                setLoading(false);
                return;
            }
            if (slugError) console.warn('Error loading rental by slug:', slugError);
            // Production IDs are UUIDs; do not send an invalid UUID query for a missing slug.
            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) {
                setRental(null);
                setLoading(false);
                return;
            }

            // Fallback to ID if slug not found
            const { data: dataById, error: idError } = await supabase
                .from('rental_aliases')
                .select(RENTAL_DETAIL_COLUMNS)
                .eq('requested_id', id)
                .maybeSingle();
            if (cancelled) return;
            if (dataById) {
                setRental(docToRental(dataById));
            } else {
                console.error('Error loading rental:', idError);
            }
            setLoading(false);
        };

        fetch();

        return () => {
            cancelled = true;
        };
    }, [id]);

    return { rental, loading };
}
